/**
 * scene.js — Three.js renderer, camera, lights, and animation loop.
 *
 * Exports: { renderer, scene, camera, startLoop, stopLoop }
 */

import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'

// ── Renderer ─────────────────────────────────────────────────────────────────
export const renderer = new THREE.WebGLRenderer({
  antialias: true,
  alpha: false,
  preserveDrawingBuffer: true, // needed for canvas screenshot (share feature)
})
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
renderer.setSize(window.innerWidth, window.innerHeight)
renderer.setClearColor(0x0a0a1a, 1)
renderer.shadowMap.enabled = true
renderer.shadowMap.type = THREE.PCFSoftShadowMap
renderer.toneMapping = THREE.ACESFilmicToneMapping
renderer.toneMappingExposure = 1.2

// Attach canvas to DOM
document.getElementById('canvas-container').appendChild(renderer.domElement)

// ── Scene ─────────────────────────────────────────────────────────────────────
export const scene = new THREE.Scene()

// Subtle fog for depth
scene.fog = new THREE.FogExp2(0x0a0a1a, 0.08)

// ── Camera ────────────────────────────────────────────────────────────────────
export const camera = new THREE.PerspectiveCamera(
  60,
  window.innerWidth / window.innerHeight,
  0.1,
  100,
)
camera.position.set(0, 0.5, 4.5)
camera.lookAt(0, 0, 0)

// ── OrbitControls (touch: pinch-zoom + single-finger rotate) ─────────────────
export const controls = new OrbitControls(camera, renderer.domElement)
controls.enableDamping = true // smooth deceleration
controls.dampingFactor = 0.08
controls.enableZoom = true // pinch-to-zoom on mobile
controls.minDistance = 2.0 // closest zoom (don't clip into crystal)
controls.maxDistance = 8.0 // farthest zoom
controls.enableRotate = true // single-finger rotate
controls.rotateSpeed = 0.6 // slightly slower for precision
controls.enablePan = false // disable two-finger pan (not needed)
controls.autoRotate = false // crystal has its own rotation in crystal.js
controls.target.set(0, 0, 0) // orbit around crystal center

// ── Lights ────────────────────────────────────────────────────────────────────
// Ambient — fills shadows
const ambient = new THREE.AmbientLight(0x1a1040, 2.0)
scene.add(ambient)

// Key light — main illumination (moves slowly to create dynamic shadows)
export const keyLight = new THREE.PointLight(0x8b7fff, 8, 20)
keyLight.position.set(3, 4, 3)
keyLight.castShadow = true
scene.add(keyLight)

// Fill light — cool blue from below
const fillLight = new THREE.PointLight(0x3060ff, 3, 15)
fillLight.position.set(-3, -2, 2)
scene.add(fillLight)

// Rim light — accent from behind (defines silhouette)
export const rimLight = new THREE.PointLight(0xff60c0, 2, 12)
rimLight.position.set(0, 0, -4)
scene.add(rimLight)

// ── Responsive resize ─────────────────────────────────────────────────────────
window.addEventListener('resize', () => {
  camera.aspect = window.innerWidth / window.innerHeight
  camera.updateProjectionMatrix()
  renderer.setSize(window.innerWidth, window.innerHeight)
})

// ── Animation loop ────────────────────────────────────────────────────────────
let _animId = null
let _callbacks = []

export function addFrameCallback(fn) {
  _callbacks.push(fn)
}

export function removeFrameCallback(fn) {
  _callbacks = _callbacks.filter((cb) => cb !== fn)
}

function _loop() {
  _animId = requestAnimationFrame(_loop)
  const t = performance.now() * 0.001 // time in seconds

  // Gently orbit key light around scene
  keyLight.position.x = Math.sin(t * 0.3) * 4
  keyLight.position.z = Math.cos(t * 0.3) * 3

  // Update OrbitControls (required for damping)
  controls.update()

  // Run per-frame callbacks (crystal rotation, particles, etc.)
  for (const cb of _callbacks) cb(t)

  renderer.render(scene, camera)
}

export function startLoop() {
  if (!_animId) _loop()
}

export function stopLoop() {
  if (_animId) {
    cancelAnimationFrame(_animId)
    _animId = null
  }
}
